import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';

interface DecodedToken {
  exp?: number;
  iat?: number;
  sub?: string;
}

const SessionValidator = ({ children }: { children: React.ReactNode }) => {
  const { token, user, signout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkSession = async () => {
      // Only run check once on mount, not on every route change
      if (!isChecking) return;
      
      // Skip validation for public routes and login pages
      const publicPaths = ['/login', '/forgot-password', '/reset-password', '/signup', '/qr', '/scan', '/menu', '/track-order', '/order/vendor', '/v/', '/vendor/whatsapp-simulator', '/channel-choice', '/checkout', '/order-success'];
      const isPublicRoute = publicPaths.some(path => location.pathname.startsWith(path));
      
      if (isPublicRoute) {
        setIsChecking(false);
        return;
      }

      // If we have a token, check if it's expired
      if (token) {
        try {
          const decoded = jwtDecode<DecodedToken>(token);
          
          // Check if token is expired
          if (decoded.exp) {
            const currentTime = Date.now() / 1000;
            if (decoded.exp < currentTime) {
              console.log('Session expired - token has expired');
              
              // Store last role for appropriate redirect
              if (user?.roles?.includes('ROLE_VENDOR')) {
                localStorage.setItem('lastUserRole', 'vendor');
              } else if (user?.roles?.includes('ROLE_STEWARD')) {
                localStorage.setItem('lastUserRole', 'steward');
              } else {
                localStorage.setItem('lastUserRole', 'customer');
              }
              
              // Clear everything and redirect
              localStorage.removeItem('token');
              localStorage.removeItem('user');
              localStorage.removeItem('guestSession');
              
              // Unified login for all users
              const redirectPath = '/login';
              
              navigate(redirectPath, { replace: true });
              window.location.reload(); // Force reload to clear all state
              return;
            }
          }
        } catch (err) {
          // If we can't decode the token, it might be invalid or a guest token
          console.log('Could not decode token, might be guest or invalid');
        }
      } else if (!isPublicRoute) {
        // No token and not on public route - redirect to unified login
        console.log('No session found, redirecting to login');
        navigate('/login', { replace: true });
      }
      
      setIsChecking(false);
    };

    checkSession();
  }, []); // Only run once on mount

  // Only show loading on initial app load, not on route changes
  if (isChecking && !token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-background-default)]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-t-transparent border-[var(--color-brand-purple)] mx-auto mb-4"></div>
          <p className="text-[var(--color-text-secondary)]">Loading...</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default SessionValidator;